import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bot, Send, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/app/widgets";

const SUGGESTIONS = [
  "Which prospects should I prioritize this week?",
  "Who should I follow up with today?",
  "Which prospects have not responded?",
  "Which partnership generated the most revenue?",
  "Which platform is producing the best results?",
  "What should I do next for DELF Success Academy?",
];

function renderInline(text, keyPrefix) {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      return (
        <Link key={`${keyPrefix}-${i}`} to={link[2]} className="font-medium text-[#D4AF37] underline decoration-[#D4AF37]/40 underline-offset-2 hover:decoration-[#D4AF37]">
          {link[1]}
        </Link>
      );
    }
    const bold = part.match(/^\*\*([^*]+)\*\*$/);
    if (bold) return <strong key={`${keyPrefix}-${i}`} className="text-white">{renderInline(bold[1], `${keyPrefix}b`)}</strong>;
    return <span key={`${keyPrefix}-${i}`}>{part}</span>;
  });
}

function MessageBody({ text }) {
  return (
    <div className="space-y-2">
      {text.split("\n").map((line, i) => {
        const t = line.trim();
        if (!t) return null;
        if (t.startsWith("###")) return <p key={i} className="pt-2 text-sm font-bold uppercase tracking-[0.14em] text-[#D4AF37]">{renderInline(t.replace(/^#+\s*/, ""), i)}</p>;
        if (t.startsWith("- ") || /^\d+\.\s/.test(t)) return <p key={i} className="pl-3 text-sm leading-relaxed text-zinc-300">{renderInline(t, i)}</p>;
        return <p key={i} className="text-sm leading-relaxed text-zinc-300">{renderInline(t, i)}</p>;
      })}
    </div>
  );
}

export default function Assistant() {
  const [messages, setMessages] = useState(null);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    api.get("/assistant/history").then((r) => setMessages(r.data.items)).catch(() => setMessages([]));
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setStreaming(true);
    const base = [...(messages || []), { role: "user", content: msg, created_at: new Date().toISOString() }];
    setMessages([...base, { role: "assistant", content: "", streaming: true }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assistant/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.detail || "Assistant error");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        const current = acc;
        setMessages([...base, { role: "assistant", content: current, streaming: true }]);
      }
      setMessages([...base, { role: "assistant", content: acc }]);
    } catch (err) {
      toast.error(apiError(err));
      setMessages(base);
    } finally {
      setStreaming(false);
    }
  };

  const clear = async () => {
    try {
      await api.delete("/assistant/history");
      setMessages([]);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (!messages) return null;

  return (
    <div data-testid="assistant-page" className="flex h-[calc(100vh-8rem)] flex-col">
      <PageHeader title="Central AI Assistant" sub="Answers grounded in your actual products, prospects, pipeline and revenue — never generic advice.">
        <button onClick={clear} data-testid="clear-chat-button" className="flex items-center gap-2 text-xs text-zinc-500 transition-colors hover:text-red-400">
          <Trash2 className="h-3.5 w-3.5" /> Clear
        </button>
      </PageHeader>

      <div className="flex-1 overflow-y-auto border border-[#27272A] bg-[#0A0A0A] p-6">
        {messages.length === 0 && (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10">
              <Bot className="h-6 w-6 text-[#D4AF37]" strokeWidth={1.5} />
            </span>
            <p className="mt-5 font-serif text-xl font-semibold">Ask your business development department.</p>
            <p className="mt-2 max-w-md text-sm text-zinc-500">
              I can see your products, campaigns, prospects, scores, outreach, follow-ups, leads and revenue.
            </p>
            <div className="mt-6 flex max-w-2xl flex-wrap justify-center gap-2">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s} onClick={() => send(s)}
                  data-testid={`suggestion-${s.slice(0, 20).replace(/\W+/g, "-").toLowerCase()}`}
                  className="rounded-full border border-white/15 px-4 py-2 text-xs text-zinc-300 transition-colors hover:border-[#D4AF37]/60 hover:text-[#D4AF37]"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mx-auto max-w-3xl space-y-6">
          {messages.map((m, i) => (
            <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
              {m.role === "user" ? (
                <div data-testid={`chat-user-${i}`} className="max-w-[85%] rounded-2xl rounded-tr-sm bg-[#D4AF37] px-5 py-3.5 text-sm font-medium text-black">
                  {m.content}
                </div>
              ) : (
                <div data-testid={`chat-assistant-${i}`} className="max-w-[92%] rounded-2xl rounded-tl-sm border border-white/10 bg-white/[0.04] px-5 py-4">
                  {m.content ? <MessageBody text={m.content} /> : null}
                  {m.streaming && (
                    <span className="mt-1 inline-flex items-center gap-2 text-xs text-zinc-500">
                      <Loader2 className="h-3 w-3 animate-spin" /> thinking…
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="mt-4 flex gap-3"
        data-testid="assistant-form"
      >
        <input
          type="text" value={input} onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about your prospects, pipeline, follow-ups, revenue…"
          data-testid="assistant-input"
          className="flex-1 rounded-full border border-[#27272A] bg-[#0A0A0A] px-5 py-3.5 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors focus:border-[#D4AF37]"
        />
        <button
          type="submit" disabled={streaming || !input.trim()}
          data-testid="assistant-send-button"
          className="flex h-12 w-12 items-center justify-center rounded-full bg-[#D4AF37] text-black transition-transform duration-300 hover:scale-105 disabled:opacity-50"
        >
          {streaming ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        </button>
      </form>
    </div>
  );
}
